import React, { useState } from 'react';
import { X, Send, Loader2, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Personal Info', description: 'Tell us about yourself' },
  { id: 2, title: 'Your Idea', description: 'What do you want to build?' },
  { id: 3, title: 'Your Background', description: 'Why you?' }
];

const PartnershipForm = ({ open, onOpenChange }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    idea: '',
    background: ''
  });
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation with specific messages
    if (!formData.name || !formData.email || !formData.idea || !formData.background) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields marked with *',
        variant: 'destructive'
      });
      return;
    }

    // Check minimum character requirements
    if (formData.idea.length < 50) {
      toast({
        title: 'Idea Too Short',
        description: `Your idea needs at least 50 characters. Currently: ${formData.idea.length} characters.`,
        variant: 'destructive'
      });
      setCurrentStep(2); // Jump to idea step
      return;
    }

    if (formData.background.length < 50) {
      toast({
        title: 'Background Too Short',
        description: `Your background needs at least 50 characters. Currently: ${formData.background.length} characters.`,
        variant: 'destructive'
      });
      setCurrentStep(3); // Jump to background step
      return;
    }

    setIsSubmitting(true);

    try {
      // Send to real API
      const response = await axios.post(`${API}/partnerships`, formData);
      
      toast({
        title: 'Application Submitted! 🎉',
        description: 'Thank you for your interest. Check your email for confirmation. We\'ll review your application and get back to you soon.',
        duration: 5000
      });

      // Reset form and close modal
      setFormData({
        name: '',
        phone: '',
        email: '',
        idea: '',
        background: ''
      });
      setCurrentStep(1);
      onOpenChange(false);
    } catch (error) {
      console.error('Partnership submission error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Something went wrong. Please try again or email us directly at gianttreefoundation26@gmail.com',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.name && formData.email;
      case 2:
        return formData.idea && formData.idea.length >= 50;
      case 3:
        return formData.background && formData.background.length >= 50;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      let errorMessage = 'Please fill in all required fields';
      
      if (currentStep === 1) {
        errorMessage = 'Please provide your name and email address';
      } else if (currentStep === 2) {
        if (!formData.idea) {
          errorMessage = 'Please describe your idea';
        } else if (formData.idea.length < 50) {
          errorMessage = `Your idea needs at least 50 characters. Currently: ${formData.idea.length} characters.`;
        }
      } else if (currentStep === 3) {
        if (!formData.background) {
          errorMessage = 'Please tell us about your background';
        } else if (formData.background.length < 50) {
          errorMessage = `Your background needs at least 50 characters. Currently: ${formData.background.length} characters.`;
        }
      }
      
      toast({
        title: 'Cannot Proceed',
        description: errorMessage,
        variant: 'destructive'
      });
      return;
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      name: '',
      phone: '',
      email: '',
      idea: '',
      background: ''
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-3xl font-bold text-gray-900">
            Have an idea that can drive social impact?
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600">
            We'd love to hear from you! Fill out this quick form and let's build something meaningful together.
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 flex-shrink-0 px-2">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-300 ${
                    currentStep > step.id
                      ? 'bg-[#4eb5e6] text-white'
                      : currentStep === step.id
                      ? 'bg-[#4eb5e6] text-white ring-4 ring-[#4eb5e6]/20'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {currentStep > step.id ? <Check className="w-6 h-6" /> : step.id}
                </div>
                <div className="mt-2 text-center">
                  <div
                    className={`text-sm font-semibold ${
                      currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                    }`}
                  >
                    {step.title}
                  </div>
                  <div
                    className={`text-xs ${
                      currentStep >= step.id ? 'text-gray-600' : 'text-gray-400'
                    }`}
                  >
                    {step.description}
                  </div>
                </div>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`h-1 flex-1 mx-4 mb-8 transition-all duration-300 ${
                    currentStep > step.id ? 'bg-[#4eb5e6]' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-2 space-y-6">
          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div className="bg-gradient-to-r from-[#4eb5e6]/10 to-[#ff8385]/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Step 1: Personal Information</h3>
                <p className="text-gray-600">Let's start with your basic details</p>
              </div>

              <div>
                <Label htmlFor="name" className="text-base font-semibold mb-2 block">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="h-14 border-2 focus:border-[#ffed00] text-base"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-base font-semibold mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="h-14 border-2 focus:border-[#ffed00] text-base"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-base font-semibold mb-2 block">
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="h-14 border-2 focus:border-[#ffed00] text-base"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>
          )}

          {/* Step 2: Your Idea */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div className="bg-gradient-to-r from-[#4eb5e6]/10 to-[#ff8385]/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Step 2: Your Idea</h3>
                <p className="text-gray-600">Tell us about the social impact you want to create</p>
              </div>

              <div>
                <Label htmlFor="idea" className="text-base font-semibold mb-2 block">
                  What are you looking to build? *
                </Label>
                <Textarea
                  id="idea"
                  required
                  value={formData.idea}
                  onChange={(e) => handleChange('idea', e.target.value)}
                  className="min-h-[200px] border-2 focus:border-[#ffed00] resize-none text-base"
                  placeholder="Describe your idea in detail:
• What problem does it solve?
• Who will it help?
• How does it promote social impact?
• What makes it unique?"
                />
                <p className={`text-sm mt-2 font-semibold ${
                  formData.idea.length < 50 ? 'text-red-500' : 'text-green-600'
                }`}>
                  {formData.idea.length} / 50 characters minimum {formData.idea.length >= 50 ? '✓' : '(required)'}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Your Background */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div className="bg-gradient-to-r from-[#4eb5e6]/10 to-[#ff8385]/10 p-6 rounded-xl">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Step 3: Your Background</h3>
                <p className="text-gray-600">Help us understand why you're the right person for this</p>
              </div>

              <div>
                <Label htmlFor="background" className="text-base font-semibold mb-2 block">
                  Why are you best suited to work on this venture? *
                </Label>
                <Textarea
                  id="background"
                  required
                  value={formData.background}
                  onChange={(e) => handleChange('background', e.target.value)}
                  className="min-h-[200px] border-2 focus:border-[#ffed00] resize-none text-base"
                  placeholder="Tell us about:
• Your relevant experience
• Skills and expertise
• Past projects or achievements
• What drives your passion for this cause"
                />
                <p className={`text-sm mt-2 font-semibold ${
                  formData.background.length < 50 ? 'text-red-500' : 'text-green-600'
                }`}>
                  {formData.background.length} / 50 characters minimum {formData.background.length >= 50 ? '✓' : '(required)'}
                </p>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">i</span>
                  </div>
                  <div>
                    <p className="text-sm text-blue-900 font-semibold">Ready to submit?</p>
                    <p className="text-sm text-blue-800">
                      Once you submit, our team will review your application and get back to you within 5-7 business days.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-100 flex-shrink-0 mt-4">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                className="flex-1 h-12 text-base border-2 font-semibold"
                disabled={isSubmitting}
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Previous
              </Button>
            )}

            {currentStep < 3 ? (
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 h-12 text-base font-bold bg-gradient-to-r from-[#4eb5e6] to-[#3da5d9] hover:from-[#3da5d9] hover:to-[#2e8fc7] text-white"
              >
                Next Step
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 h-12 text-base font-bold bg-gradient-to-r from-[#ff8385] to-[#ff6b6d] hover:from-[#ff6b6d] hover:to-[#ff5356] text-white"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnershipForm;
